/**
 * Section Agreement Tab for A/B Testing ResultsDashboard
 *
 * Sprint 11e Package 3: Displays agreement metrics per IAB section.
 *
 * Shows a heatmap of how models agree on each section:
 * - Demographics: Age, gender, education, etc.
 * - Household: Home ownership, family status, etc.
 * - Interests: Hobbies, preferences, etc.
 * - Purchase Intent: Shopping behavior, etc.
 *
 * @see docs/sprints/ownyou-sprint11e-spec.md
 */

import { useMemo } from 'react'
import type { RichModelResults, IABSection } from '@ownyou/ab-testing'
import { computeAllSectionAgreements } from '@ownyou/ab-testing'

interface SectionAgreementTabProps {
  results: Map<string, RichModelResults>
}

const SECTIONS: IABSection[] = ['demographics', 'household', 'interests', 'purchase_intent']

const SECTION_LABELS: Record<IABSection, string> = {
  demographics: 'Demographics',
  household: 'Household',
  interests: 'Interests',
  purchase_intent: 'Purchase Intent',
}

const SECTION_COLORS: Record<IABSection, { bg: string; text: string; border: string }> = {
  demographics: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200' },
  household: { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200' },
  interests: { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200' },
  purchase_intent: { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200' },
}

function formatModelName(modelKey: string): string {
  // Split on first colon only - model names may contain colons (e.g., Ollama "qwen3:8b")
  const [provider, ...modelParts] = modelKey.split(':')
  const model = modelParts.join(':')
  const shortModel = model?.split('/').pop() || model
  return `${provider?.charAt(0).toUpperCase()}${provider?.slice(1)} ${shortModel}`
}

function getShortModelName(modelKey: string): string {
  // Split on first colon only - model names may contain colons (e.g., Ollama "qwen3:8b")
  const [, ...modelParts] = modelKey.split(':')
  const model = modelParts.join(':')
  const shortModel = model?.split('/').pop() || model
  return shortModel?.substring(0, 12) || modelKey.substring(0, 12)
}

function getHeatmapColor(value: number): string {
  const intensity = Math.floor(value * 255)
  return `rgb(${255 - intensity}, 255, ${255 - intensity})`
}

function getAgreementColor(value: number): string {
  if (value >= 0.8) return 'text-green-600'
  if (value >= 0.6) return 'text-yellow-600'
  return 'text-red-600'
}

export function SectionAgreementTab({ results }: SectionAgreementTabProps) {
  const modelKeys = useMemo(() => Array.from(results.keys()), [results])

  // Check if we have rich classification data
  const hasRichData = useMemo(() => {
    for (const [, r] of results) {
      if (r.sectionBreakdown) return true
    }
    return false
  }, [results])

  // Compute section agreements
  const sectionAgreements = useMemo(() => {
    if (!hasRichData) return null
    return computeAllSectionAgreements(results)
  }, [results, hasRichData])

  if (!hasRichData) {
    return (
      <div className="space-y-6">
        <h3 className="text-lg font-semibold">Section Agreement Analysis</h3>
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
          <div className="text-yellow-800 font-medium mb-2">
            Section Agreement Data Not Available
          </div>
          <p className="text-yellow-700 text-sm">
            Section agreement metrics require the full IAB classifier workflow (Sprint 11e).
            <br />
            The current results were generated using simple prompt classification.
          </p>
        </div>
      </div>
    )
  }

  if (!sectionAgreements) return null

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold">Section Agreement Analysis</h3>

      {/* Section summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {SECTIONS.map((section) => {
          const agreement = sectionAgreements[section]
          const colors = SECTION_COLORS[section]
          return (
            <div
              key={section}
              className={`${colors.bg} rounded-lg p-4 border ${colors.border}`}
            >
              <div className={`text-sm font-medium ${colors.text} mb-2`}>
                {SECTION_LABELS[section]}
              </div>
              <div className={`text-2xl font-bold ${colors.text}`}>
                {(agreement.agreementRate * 100).toFixed(0)}%
              </div>
              <div className="text-xs text-gray-500 mt-1">
                {agreement.fullAgreementCount} full / {agreement.partialAgreementCount} partial
              </div>
            </div>
          )
        })}
      </div>

      {/* Per-section pairwise agreement matrices */}
      {SECTIONS.map((section) => {
        const agreement = sectionAgreements[section]
        const colors = SECTION_COLORS[section]

        return (
          <div key={section} className="border rounded-lg overflow-hidden">
            <div className={`${colors.bg} px-4 py-3 border-b ${colors.border}`}>
              <h4 className={`font-medium ${colors.text}`}>
                {SECTION_LABELS[section]} Agreement Matrix
              </h4>
              <div className="flex gap-4 mt-1 text-xs text-gray-600">
                <span>Full: {agreement.fullAgreementCount}</span>
                <span>Partial: {agreement.partialAgreementCount}</span>
                <span>None: {agreement.noAgreementCount}</span>
                <span className={`font-medium ${getAgreementColor(agreement.agreementRate)}`}>
                  Rate: {(agreement.agreementRate * 100).toFixed(1)}%
                </span>
              </div>
            </div>

            <div className="p-4 overflow-x-auto">
              <table className="min-w-full border">
                <thead>
                  <tr>
                    <th className="border px-2 py-1 bg-gray-50 text-xs"></th>
                    {modelKeys.map((key) => (
                      <th
                        key={key}
                        className="border px-2 py-1 bg-gray-50 text-xs truncate max-w-24"
                        title={formatModelName(key)}
                      >
                        {getShortModelName(key)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {modelKeys.map((rowKey) => (
                    <tr key={rowKey}>
                      <td
                        className="border px-2 py-1 bg-gray-50 text-xs font-medium truncate max-w-24"
                        title={formatModelName(rowKey)}
                      >
                        {getShortModelName(rowKey)}
                      </td>
                      {modelKeys.map((colKey) => {
                        const value = agreement.pairwiseAgreement[rowKey]?.[colKey] || 0
                        return (
                          <td
                            key={colKey}
                            className="border px-2 py-1 text-center text-xs"
                            style={{ backgroundColor: getHeatmapColor(value) }}
                          >
                            {(value * 100).toFixed(0)}%
                          </td>
                        )
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )
      })}

      {/* Legend */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="font-medium text-gray-700 mb-2">Understanding the Metrics</h4>
        <div className="grid md:grid-cols-3 gap-4 text-sm text-gray-600">
          <div>
            <span className="font-medium text-green-600">Full Agreement:</span>
            <span className="ml-1">All models assign the same classification</span>
          </div>
          <div>
            <span className="font-medium text-yellow-600">Partial Agreement:</span>
            <span className="ml-1">Majority of models agree</span>
          </div>
          <div>
            <span className="font-medium text-red-600">No Agreement:</span>
            <span className="ml-1">No majority classification</span>
          </div>
        </div>
      </div>
    </div>
  )
}
